import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { StatusAccessService } from 'src/app/services/status-access.service';
import { UserAccessStatus } from './user-access-status.model';
import { AccessStatus } from '../box-status-access/box-status-access.component';
import { ConfirmSyncDialogComponent } from '../confirm-sync-dialog/confirm-sync-dialog.component';

interface AccessBox {
  title: string;
  icon: string;
  description: string;
  status: AccessStatus;
  actionEnabled: boolean;
  loading: boolean;
  errorMessage: string | null;
  syncFn: () => void;
}

@Component({
  selector: 'app-tab-status-acesso',
  templateUrl: './tab-status-acesso.component.html',
  styleUrls: ['./tab-status-acesso.component.scss'],
})
export class TabStatusAcessoComponent implements OnChanges {
  @Input() idNewWizer: number | null = null;

  loadingStatus: boolean = false;
  accessBoxes: AccessBox[] = [];
  userEmail: string | null = null;

  constructor(
    private readonly statusAccessService: StatusAccessService,
    private readonly dialog: MatDialog
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['idNewWizer'] && this.idNewWizer) {
      this.loadingStatus = false
      this.loadStatus();
    }
  }

  private loadStatus(): void {
    this.loadingStatus = true;
    this.statusAccessService.getUserAccessStatus(this.idNewWizer!).subscribe(
      (data: UserAccessStatus) => {
        this.buildBoxes(data);
        this.loadingStatus = false;
      },
      () => {
        this.loadingStatus = false;
      }
    );
  }

  private buildBoxes(data: UserAccessStatus): void {
    this.userEmail = data.email && data.email.trim().length > 0 ? data.email : null;

    this.accessBoxes = [
      {
        title: 'SSO',
        icon: 'fas fa-key',
        description: 'Acesso para fazer login em múltiplos sistemas.',
        status: data.ssoOk ? 'ativo' : 'pendente',
        actionEnabled: !data.ssoOk,
        loading: false,
        errorMessage: null,
        syncFn: () => this.syncSso(),
      },
      {
        title: 'Azure AD',
        icon: 'fab fa-microsoft',
        description:
          'Acesso de recursos internos (intranet) e externos (Microsoft 365, Teams e Nuvem Azure).',
        status: data.adOk ? 'ativo' : 'pendente',
        actionEnabled: !data.adOk,
        loading: false,
        errorMessage: null,
        syncFn: () => this.syncAd(),
      },
      {
        title: 'Email corporativo na LG',
        icon: 'fas fa-users',
        description: 'Tabela de funcionários.',
        status: data.viewFuncionariosOk ? 'ativo' : 'pendente',
        actionEnabled: !data.viewFuncionariosOk,
        loading: false,
        errorMessage: null,
        syncFn: () => this.syncEmployee(),
      },
      {
        title: 'Wizity',
        icon: 'fa fa-address-card',
        description: 'Acesso para fazer login na wizity.',
        status: data.wizityOk ? 'ativo' : 'pendente',
        actionEnabled: !data.wizityOk,
        loading: false,
        errorMessage: null,
        syncFn: () => this.syncWizity(),
      },
    ];
  }

  onAtualizar(box: AccessBox): void {
    const dialogRef = this.dialog.open(ConfirmSyncDialogComponent, {
      width: '420px',
      minWidth: '500px',
      data: { title: box.title, email: this.userEmail },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      box.loading = true;
      box.errorMessage = null;
      box.syncFn();
    });
  }

  private handleSyncError(box: AccessBox, error: any): void {
    box.loading = false;

    let message = 'Erro ao sincronizar. Tente novamente.';
    try {
      const detail = error?.detail ?? error?.error?.detail;
      if (detail) {
        const parsed = JSON.parse(detail);
        message = Array.isArray(parsed) ? parsed.join(', ') : String(parsed);
      }
    } catch {
      // mantém mensagem padrão
    }

    box.errorMessage = message;
    setTimeout(() => (box.errorMessage = null), 6000);
  }

  private syncSso(): void {
    const ssoBox = this.accessBoxes[0];
    this.statusAccessService.syncSso(this.idNewWizer!).subscribe(
      () => {
        ssoBox.loading = false;
        this.loadStatus();
      },
      (error) => this.handleSyncError(ssoBox, error)
    );
  }

  private syncAd(): void {
    const adBox = this.accessBoxes[1];
    this.statusAccessService.syncAd(this.idNewWizer!).subscribe(
      () => {
        adBox.loading = false;
        this.loadStatus();
      },
      (error) => this.handleSyncError(adBox, error)
    );
  }

   private syncWizity(): void {
    const wizityBox = this.accessBoxes[3];
    this.statusAccessService.syncWizity(this.idNewWizer!).subscribe(
      () => {
        wizityBox.loading = false;
        this.loadStatus();
      },
      (error) => this.handleSyncError(wizityBox , error)
    );
  }



  private syncEmployee(): void {
    const employeeBox = this.accessBoxes[2];
    this.statusAccessService.syncEmployee(this.idNewWizer!).subscribe(
      () => {
        employeeBox.loading = false;
        this.loadStatus();
      },
      (error) => this.handleSyncError(employeeBox, error)
    );
  }
}
